import type { AgencyUsageAllowances, AgencyUsageReport } from '~/api/graphql';
import { formatUsageUnits, parseUsageValue } from '~/lib/usage';
import { SeatProgressBar } from './seat-progress-bar';

type MeterRow = {
  label: string;
  used: number;
  included: number;
  unitLabel: string;
};

const MeterCard = ({ label, used, included, unitLabel }: MeterRow) => {
  const overBy = Math.max(0, used - included);
  const total = overBy > 0 ? used : Math.max(included, 1);
  const active = overBy > 0 ? included : used;

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">{label}</h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {formatUsageUnits(used)} / {formatUsageUnits(included)} included
        </p>
        <div className="mt-4">
          <SeatProgressBar
            totalSeats={total}
            activeSeats={active}
            childAllocatedSeats={overBy > 0 ? overBy : undefined}
            size="sm"
            labels={{
              active: (count) =>
                overBy > 0
                  ? `${formatUsageUnits(count)} included`
                  : `${formatUsageUnits(count)} used`,
              childAllocated: (count) => `${formatUsageUnits(count)} ${unitLabel} overage`,
              available: (count) => `${formatUsageUnits(count)} remaining`
            }}
          />
        </div>
      </div>
    </div>
  );
};

type UsageMetersProps = {
  report: Pick<AgencyUsageReport, 'platformUnits' | 'voiceMinutes' | 'kbPages'> | null | undefined;
  allowances: AgencyUsageAllowances | null | undefined;
};

export const UsageMeters = ({ report, allowances }: UsageMetersProps) => {
  if (!report || !allowances) {
    return (
      <div className="rounded-lg bg-white dark:bg-neutral-800 px-6 py-10 text-center shadow">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Usage data is not available for this billing period yet.
        </p>
      </div>
    );
  }

  const rows: MeterRow[] = [
    {
      label: 'Platform units',
      used: parseUsageValue(report.platformUnits.used),
      included: allowances.platformUnits.included,
      unitLabel: allowances.platformUnits.unitLabel
    },
    {
      label: 'Knowledge base pages',
      used: parseUsageValue(report.kbPages.used),
      included: allowances.kbPages.included,
      unitLabel: allowances.kbPages.unitLabel
    },
    {
      label: 'Voice minutes',
      used: parseUsageValue(report.voiceMinutes.used),
      included: allowances.voiceMinutes.included,
      unitLabel: allowances.voiceMinutes.unitLabel
    }
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((row) => (
        <MeterCard key={row.label} {...row} />
      ))}
    </div>
  );
};
